import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { autoExcerpt } from "@/lib/markdown";
import PageHead from "@/components/PageHead";
import NewsletterSignup from "@/components/NewsletterSignup";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

export default function Journal() {
  const [posts, setPosts] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    api
      .get("/journal")
      .then((r) => mounted && setPosts(r.data))
      .catch(() => mounted && setError("The journal could not load right now."));
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <div data-testid="blog-page">
      <PageHead
        title="Blog — Redkey Web Design"
        description="Field notes on custom web development, technical SEO, accessibility (WCAG 2.2), Drupal & WordPress, and AI automation — from a working senior developer."
        canonical="https://redkeyweb.design/blog"
      />

      {/* Header */}
      <section className="mx-auto max-w-[1360px] px-6 md:px-10 pt-16 md:pt-24 pb-10">
        <p className="eyebrow">§ Field Notes · Web Design · SEO · Drupal · AI</p>
        <h1
          data-testid="blog-title"
          className="font-display text-5xl md:text-7xl mt-6 max-w-[18ch]"
        >
          The <em className="not-italic text-brand-red">blog.</em>
        </h1>
        <p className="mt-6 max-w-[62ch] text-lg text-ink-soft">
          Working notes on the craft of custom web development — accessibility,
          performance, technical SEO, government Drupal, WordPress, and the
          places AI automation earns its keep. Written by hand, not by a
          template.
        </p>
      </section>

      {/* Body */}
      <section className="mx-auto max-w-[1360px] px-6 md:px-10 border-t hairline pt-12 pb-24">
        {error && (
          <div
            className="border hairline-strong bg-paper-alt p-6"
            data-testid="blog-error"
          >
            <p className="eyebrow">Blog unavailable</p>
            <p className="mt-3 font-body text-ink">{error}</p>
          </div>
        )}

        {!error && posts === null && (
          <p className="font-body text-ink-soft" data-testid="blog-loading">
            Loading the archive…
          </p>
        )}

        {!error && posts !== null && posts.length === 0 && (
          <div
            className="border hairline-strong bg-paper-alt p-8"
            data-testid="blog-empty"
          >
            <p className="eyebrow">§ Coming soon</p>
            <h2 className="font-display text-3xl md:text-4xl mt-4">
              The first entry is on the way.
            </h2>
            <p className="mt-4 font-body text-ink-soft max-w-prose">
              We&rsquo;re drafting a small library of long-form field notes on
              custom builds, Drupal migrations, and accessible design. Check
              back shortly, or subscribe by dropping us a line.
            </p>
          </div>
        )}

        {!error && posts !== null && posts.length > 0 && (
          <ul
            className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-16"
            data-testid="blog-list"
          >
            {posts.map((p, idx) => (
              <li key={p.id} data-testid={`blog-item-${p.slug}`}>
                <Link to={`/blog/${p.slug}`} className="block group">
                  {p.cover_image ? (
                    <div className="aspect-[16/10] overflow-hidden bg-paper-alt border hairline">
                      <img
                        src={p.cover_image}
                        alt={`${p.title} — cover`}
                        loading={idx < 2 ? "eager" : "lazy"}
                        decoding="async"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                      />
                    </div>
                  ) : (
                    <div className="aspect-[16/10] border hairline-strong bg-paper flex items-center justify-center">
                      <span className="font-display text-6xl text-ink/10 select-none">
                        R
                      </span>
                    </div>
                  )}
                  <div className="mt-5 flex flex-wrap items-baseline gap-3">
                    <p className="eyebrow">
                      {formatDate(p.published_at || p.created_at)}
                    </p>
                    {p.keywords && p.keywords.length > 0 && (
                      <p className="font-ui text-[11px] text-label tracking-widest uppercase">
                        {p.keywords.slice(0, 3).join(" · ")}
                      </p>
                    )}
                  </div>
                  <h2 className="font-display text-3xl md:text-4xl mt-3 group-hover:text-electric transition-colors">
                    {p.title}
                  </h2>
                  <p className="mt-4 font-body text-ink-soft leading-relaxed max-w-prose">
                    {p.excerpt || autoExcerpt(p.body)}
                  </p>
                  <span className="mt-4 inline-flex items-center gap-2 font-ui text-sm text-brand-red-ink">
                    Read the entry <span aria-hidden>→</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Newsletter signup — appears on the archive footer */}
      <section className="mx-auto max-w-[1360px] px-6 md:px-10 pb-24">
        <NewsletterSignup variant="wide" surface="alt" />
      </section>
    </div>
  );
}
